import React from 'react';
import { FormGroup } from 'reactstrap';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import {
    FormCheckbox, FormLabel, FlexRow, InfoCheckbox,
    NumericInput, Input,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SlideForm from '../common/forms/SlideForm';
import CommonContext, { ChargeTypeUnits } from '../Common';
import { ChargeTypeUnitsName } from './ChargeType';

export default class ChargeTypeConfigurationForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: true,
            errors: {},
            formValidated: false,
            validationMessage: '',
            isValidated: false,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onSubmit = () => {
        this.context.setFormOpened(false);
        this.props.onSaveCallback();
    };
    onRemove = () => {
        this.context.setFormOpened(false);
        this.props.onRemoveCallback();
    };

    populateState() {
        this.setState({
            loading: false,
        });
    }

    resetForm = () => this.setState({ formValidated: false, errors: {} });

    selectAllCustomers = (availableCustomers) => {
        const name = (this.props.entityName ? `${this.props.entityName}.` : '');
        this.props.onSelectChange(availableCustomers, { name: `${name}customers` });
    };

    getAvailableCustomers = () => {
        const { customers, existingExceptions, entity } = this.props;
        if (!customers) return [];

        // Get all customer IDs used in OTHER exceptions (not the current one being edited)
        const usedCustomerIds = new Set(
            (existingExceptions ?? [])
                .filter((ex) => ex.id !== entity?.id) // Exclude current exception
                .flatMap((ex) => ex.customers ?? []),
        );

        // Filter out already-used customers
        return customers.filter((c) => !usedCustomerIds.has(c.value));
    };

    render() {
        if (!this.props.entity) return '';
        const {
            entity, hasHourlyEquipment, surcharges, isBillable,
        } = this.props;
        const name = (this.props.entityName ? `${this.props.entityName}.` : '');
        const unitsName = ChargeTypeUnitsName[this.props.entity.unitsId] || '';
        const availableCustomers = this.getAvailableCustomers();

        return (
            <SlideForm
                loading={this.state.loading}
                show={this.props.show}
                id="chargeTypeConfigForm"
                formIcon={faMinusCircle}
                formTitle={!this.props.isNewException ? 'Edit Exception' : 'Add Exception'}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={this.state.formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={this.state.validationMessage}
                showDelete={!this.props.isNewException}
                onDelete={this.onRemove}
            >
                <>
                    <ValidatedSelect
                        name={`${name}customers`}
                        label="Customers"
                        isMulti
                        required
                        options={availableCustomers}
                        value={(this.props.customers ?? []).filter((x) => (entity.customers ?? []).includes(x.value)) ?? ''}
                        onChange={this.props.onSelectChange}
                        onSelectAll={() => this.selectAllCustomers(availableCustomers)}
                        error="At least one group selection is required."
                    />

                    <FormGroup>
                        <FormLabel text="Applicable Charge?" />
                        <FormCheckbox
                            id={`${name}doesNotApply`}
                            className="ml-3"
                            checked={entity.doesNotApply ?? ''}
                            onChange={this.props.onDoesNotApplyChanged}
                            labelText="Does Not Apply"
                        />
                    </FormGroup>

                    {!entity.doesNotApply
                        && (
                            <>
                                {this.props.chargeUnitId == ChargeTypeUnits.Hours && (
                                    <>
                                        <ValidatedSelect
                                            name={`${name}increment`}
                                            label="Increment"
                                            required={this.props.requireIncrement}
                                            options={this.props.increments}
                                            value={this.props.increments.find((i) => i.value == entity.increment) ?? ''}
                                            onChange={this.props.onSelectChange}
                                            error="Increment is required."
                                        />

                                        <FormGroup>
                                            <FormLabel label="Break Options" />

                                            <InfoCheckbox
                                                label="Enable Breaks"
                                                id={`${name}canBreak`}
                                                name={`${name}canBreak`}
                                                checked={entity.canBreak ?? ''}
                                                onChange={this.props.onCanBreakChanged}
                                            >
                                                <InfoCheckbox
                                                    id={`${name}paidBreak`}
                                                    name={`${name}paidBreak`}
                                                    disabled={!entity.canBreak}
                                                    checked={entity.paidBreak ?? ''}
                                                    onChange={this.props.onChange}
                                                    label="Paid Breaks"
                                                    color="dark"
                                                    tooltip="Selecting this option will cause breaks to not be deducted from related charges."
                                                />

                                                <Input
                                                    label="Break Description"
                                                    name={`${name}breakDescription`}
                                                    value={entity.breakDescription ?? ''}
                                                    onChange={this.props.onChange}
                                                    placeholder="Break description"
                                                    required={!!entity.canBreak}
                                                    error="Break Description is required."
                                                />

                                                {!entity.isSurcharge && (
                                                    <InfoCheckbox
                                                        id={`${name}addSurchargeIfMissedBreak`}
                                                        name={`${name}addSurchargeIfMissedBreak`}
                                                        disabled={!entity.canBreak}
                                                        checked={entity.addSurchargeIfMissedBreak ?? ''}
                                                        onChange={this.props.onChange}
                                                        label="Add Surcharge If Break Is Missed"
                                                        tooltip="A surcharge will only be added for missed lunch breaks if the employee works for at least a number of hours equal to the below threshold."
                                                    >
                                                        <NumericInput
                                                            label="Missed Lunch Break Hours Threshold"
                                                            name={`${name}missedBreakHoursThreshold`}
                                                            required={Boolean(entity.addSurchargeIfMissedBreak)}
                                                            value={entity.missedBreakHoursThreshold ?? ''}
                                                            onChange={this.props.onChange}
                                                            step="0.25"
                                                            min={0}
                                                            error="A work hour threshold is required."
                                                        />

                                                        <ValidatedSelect
                                                            name={`${name}childChargeTypeId`}
                                                            id={`${name}childChargeTypeId`}
                                                            label="Surcharge to Add On Missed Break"
                                                            isClearable
                                                            required={Boolean(entity.addSurchargeIfMissedBreak)}
                                                            options={surcharges}
                                                            value={
                                                                (surcharges ?? []).find(
                                                                    (x) => x.value === entity.childChargeTypeId,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.props.onSelectChange}
                                                            error="A surcharge selection is required."
                                                        />
                                                    </InfoCheckbox>
                                                )}
                                            </InfoCheckbox>
                                        </FormGroup>
                                        {isBillable && (
                                            <FormCheckbox
                                                id={`${name}doNotApplyMinimumBill`}
                                                name={`${name}doNotApplyMinimumBill`}
                                                checked={entity.doNotApplyMinimumBill ?? ''}
                                                onChange={this.props.onChange}
                                                labelText="Do Not Apply Minimum Bill"
                                            />
                                        )}
                                        {(!entity.doNotApplyMinimumBill || !isBillable) && (
                                            <NumericInput
                                                label="Minimum Bill"
                                                name={`${name}minimumBillUnits`}
                                                required={!!entity.minimumBillUnits}
                                                value={entity.minimumBillUnits ?? ''}
                                                onChange={this.props.onChange}
                                                placeholder={`Enter ${unitsName}`}
                                                step="0.25"
                                                error="Minimum Bill is required."
                                            />
                                        )}
                                        <NumericInput
                                            label="Maximum Bill"
                                            name={`${name}maximumBillUnits`}
                                            required={!!entity.maximumBillUnits}
                                            value={entity.maximumBillUnits ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder={`Enter ${unitsName}`}
                                            step="0.25"
                                            error="Maximum Bill is required."
                                        />
                                    </>
                                )}

                                {this.props.chargeUnitId == ChargeTypeUnits.Flat && (
                                    <>
                                        <NumericInput
                                            label="Increment"
                                            name={`${name}increment`}
                                            required={this.props.requireIncrement}
                                            value={entity.increment ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder={`Enter ${unitsName}`}
                                            step="0.25"
                                            error="Increment is required."
                                        />
                                        <NumericInput
                                            label="Minimum Bill"
                                            name={`${name}minimumBillUnits`}
                                            required={!!entity.minimumPayUnits}
                                            value={entity.minimumBillUnits ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder={`Enter ${unitsName}`}
                                            step="0.25"
                                            error="Minimum Bill is required."
                                        />
                                        <NumericInput
                                            label="Maximum Bill"
                                            name={`${name}maximumBillUnits`}
                                            required={!!entity.maximumPayUnits}
                                            value={entity.maximumBillUnits ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder={`Enter ${unitsName}`}
                                            step="0.25"
                                            error="Maximum Bill is required."
                                        />
                                    </>
                                )}
                                <FormGroup>
                                    <FormLabel
                                        label="Timesheet Options"
                                    />
                                    <FormCheckbox
                                        className="ml-3"
                                        id={`${name}disallowNA`}
                                        name={`${name}disallowNA`}
                                        checked={entity.disallowNA ?? ''}
                                        onChange={this.props.onChange}
                                        labelText="Require Data Entry on Timesheet (Do not allow N/A reply)"
                                    />
                                </FormGroup>
                                {this.props.chargeUnitId != ChargeTypeUnits.Percentage && (
                                    <>
                                        <FormGroup>
                                            <FormLabel label="Cancellation Options" />
                                            {(this.props.tenantSettings ?? {}).blockBillingCancelEnabled && (
                                                <FormGroup>
                                                    <FlexRow className="ml-3">
                                                        <FormCheckbox
                                                            id={`${name}blockBillingCancel`}
                                                            name={`${name}blockBillingCancel`}
                                                            checked={entity.blockBillingCancel ?? ''}
                                                            onChange={this.props.onChange}
                                                            labelText="Block Cancellation if Min Bill Qty Charge > 0 for Billing"
                                                        />
                                                    </FlexRow>
                                                </FormGroup>
                                            )}
                                            {(this.props.tenantSettings ?? {}).blockPayrollCancelEnabled && (
                                                <FormGroup>
                                                    <FlexRow className="ml-3">
                                                        <FormCheckbox
                                                            id={`${name}blockPayrollCancel`}
                                                            name={`${name}blockPayrollCancel`}
                                                            checked={entity.blockPayrollCancel ?? ''}
                                                            onChange={this.props.onChange}
                                                            labelText="Block Cancellation if Min Pay Qty Charge > 0 for Payroll"
                                                        />
                                                    </FlexRow>
                                                </FormGroup>
                                            )}
                                        </FormGroup>
                                        <NumericInput
                                            formGroupClassName="ml-3"
                                            label="Cancellation Time Threshold (Min)"
                                            id={`${name}cancellationTimeThreshold`}
                                            name={`${name}cancellationTimeThreshold`}
                                            value={entity.cancellationTimeThreshold ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder="Enter Time in Minutes"
                                            step="1"
                                            required={!!entity.canBreak}
                                        />
                                        <NumericInput
                                            formGroupClassName="ml-3"
                                            label={`Minimum Cancellation Bill Qty (${unitsName})`}
                                            id={`${name}cancellationBillQty`}
                                            name={`${name}cancellationBillQty`}
                                            value={entity.cancellationBillQty ?? ''}
                                            onChange={this.props.onChange}
                                            placeholder={`Enter Qty ${unitsName}`}
                                            step=".025"
                                            required={!!entity.canBreak}
                                        />
                                    </>
                                )}

                                <FormGroup>
                                    <FormLabel
                                        label="Invoicing"
                                    />
                                    <FormCheckbox
                                        id={`${name}omitFromInvoice`}
                                        name={`${name}omitFromInvoice`}
                                        className="ml-3"
                                        checked={entity.omitFromInvoice ?? ''}
                                        onChange={this.props.onChange}
                                        labelText="Omit from Charge Details on Invoice"
                                    />
                                </FormGroup>

                                {hasHourlyEquipment && (
                                    <FormGroup>
                                        <FormLabel
                                            label="Hourly Equipment"
                                        />
                                        <FormCheckbox
                                            id={`${name}hourlyEquipmentOvertime`}
                                            name={`${name}hourlyEquipmentOvertime`}
                                            className="ml-3"
                                            checked={
                                                entity.hourlyEquipmentOvertime ?? false
                                            }
                                            onChange={this.props.onChange}
                                            labelText="Overtime applies to hourly equipment charge"
                                        />
                                    </FormGroup>
                                )}
                            </>
                        )}
                </>
            </SlideForm>
        );
    }
}
